/**
 * Class to implement the picture functionality
 * for a single instance; the application is done
 * via the PictureManager class
 */
export class Picture {
    private readonly sources: HTMLSourceElement[];
    private observer: MutationObserver | null = null;
    private rafId: number | null = null;

    private static readonly DATA_ORIGINAL_MEDIA = "originalMedia";

    /**
     * Constructor of the Picture
     *
     * @param {HTMLPictureElement} picture
     */
    public constructor(private picture: HTMLPictureElement) {
        // Find all sources of the picture element
        this.sources = Array.from(this.picture.querySelectorAll("source"));

        // Snapshot each source's original media once
        this.sources.forEach((s) => {
            if (!s.dataset[Picture.DATA_ORIGINAL_MEDIA]) {
                const original = s.getAttribute("media");
                s.dataset[Picture.DATA_ORIGINAL_MEDIA] =
                    original === null ? "__NULL__" : original;
            }
        });

        this.useSelectedTheme();
    }

    /**
     * Method to handle the toggling via mouse-click
     *
     * @returns {void}
     */
    private useSelectedTheme(): void {
        // Initial pass
        this.apply();

        // Observe <html data-theme> changes
        this.observer = new MutationObserver((mutations) => {
            if (
                mutations.some((mutation) => {
                    return (
                        mutation.type === "attributes" &&
                        mutation.attributeName === "data-theme"
                    );
                })
            ) {
                this.apply();
            }
        });
        this.observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ["data-theme"],
        });

        // Resize (rAF-throttled)
        window.addEventListener("resize", this.scheduleApply, {
            passive: true,
        });
    }

    /**
     * Method to destroy any listeners and observers
     * and bring everything back to it's initial state
     *
     * @returns {void}
     */
    public destroy(): void {
        window.removeEventListener("resize", this.scheduleApply);
        this.observer?.disconnect();
        if (this.rafId != null) cancelAnimationFrame(this.rafId);
    }

    /**
     * Method to apply the evaluated state to each source
     *
     * @returns {void}
     */
    private apply = (): void => {
        const theme = this.getForcedTheme();

        // Always evaluate using each source's original media
        const evaluations = this.sources.map((source) => {
            const originalMedia = this.getOriginalMedia(source);
            const matchesViewport = this.matchesMedia(originalMedia);
            const sourceTheme = this.getSourceTheme(source);

            return { source, originalMedia, matchesViewport, sourceTheme };
        });

        if (theme === null) {
            // No forced theme:
            // restore the original media for all sources (no filtering)
            for (const evaluation of evaluations) {
                this.setEnabled(
                    evaluation.source,
                    true,
                    evaluation.originalMedia,
                );
            }
            return;
        }

        // Forced theme path
        const matching = evaluations.filter((e) => e.matchesViewport);
        const themedMatching = matching.filter((e) => e.sourceTheme === theme);
        const useThemedOnly = themedMatching.length > 0;

        for (const evaluation of evaluations) {
            if (!evaluation.matchesViewport) {
                // Off-viewport:
                // keep the original media value so
                // it can engage naturally on resize
                this.setEnabled(
                    evaluation.source,
                    true,
                    evaluation.originalMedia,
                );
                continue;
            }

            if (useThemedOnly) {
                this.setEnabled(
                    evaluation.source,
                    evaluation.sourceTheme === theme,
                    evaluation.originalMedia,
                );
            } else {
                // No specifically themed match
                // allow 'any' or same-theme if present, block the opposite-theme
                if (
                    evaluation.sourceTheme === "any" ||
                    evaluation.sourceTheme === theme
                ) {
                    this.setEnabled(
                        evaluation.source,
                        true,
                        evaluation.originalMedia,
                    );
                } else {
                    this.setEnabled(
                        evaluation.source,
                        false,
                        evaluation.originalMedia,
                    );
                }
            }
        }
    };

    /**
     * Method to get the forced theme
     *
     * @returns {"light" | "dark" | null}
     */
    private getForcedTheme(): "light" | "dark" | null {
        const v = (document.documentElement.getAttribute("data-theme") || "")
            .trim()
            .toLowerCase();

        return v === "light" || v === "dark" ? v : null;
    }

    /**
     * Method to get the theme to which the provided source applies
     *
     * @param {HTMLSourceElement} source
     * @returns {"light" | "dark" | "any"}
     */
    private getSourceTheme(
        source: HTMLSourceElement,
    ): "light" | "dark" | "any" {
        const v = (source.getAttribute("data-theme") || "")
            .trim()
            .toLowerCase();
        return v === "light" || v === "dark" ? v : "any";
    }

    /**
     * Method to get the original media value for a source element
     *
     * @param {HTMLSourceElement} source
     * @returns {string | null}
     */
    private getOriginalMedia(source: HTMLSourceElement): string | null {
        const raw = source.dataset[Picture.DATA_ORIGINAL_MEDIA];
        if (raw === undefined) return source.getAttribute("media");
        return raw === "__NULL__" ? null : raw;
    }

    /**
     * Method to check if a media value matches the window
     *
     * @param {string | null} media
     * @returns {boolean}
     */
    private matchesMedia(media: string | null): boolean {
        if (!media || media.trim() === "") return true;

        try {
            return window.matchMedia(media).matches;
        } catch {
            // Invalid media query;
            // treat as non-matching
            return false;
        }
    }

    /**
     * Method to enable or disable a source element by restoring
     * the original media value or by setting media to "not all"
     *
     * @param {HTMLSourceElement} source
     * @param {boolean} enabled
     * @param {string | null} originalMedia
     * @returns {void}
     */
    private setEnabled(
        source: HTMLSourceElement,
        enabled: boolean,
        originalMedia: string | null,
    ): void {
        if (enabled) {
            if (originalMedia === null) {
                source.removeAttribute("media");
            } else {
                source.setAttribute("media", originalMedia);
            }
        } else {
            source.setAttribute("media", "not all");
        }
    }

    /**
     * Method to schedule the application using
     * animation frame requests to keep it performant
     *
     * @return {void}
     */
    private scheduleApply = (): void => {
        if (this.rafId != null) cancelAnimationFrame(this.rafId);
        this.rafId = requestAnimationFrame(() => {
            this.rafId = null;
            this.apply();
        });
    };
}
